/**
 * ProviderLogo — real full-color provider brand marks for the integrations
 * catalog.
 *
 * Renders the official product logo (full color, product-distinct — e.g.
 * Microsoft Teams vs Outlook vs 365, Google Calendar vs Meet vs Maps) from
 * Iconify's open-source `logos` set on a light neutral tile. Providers with no
 * mapped logo (or a mark that fails to load) fall back to the brand-colored
 * glyph tile.
 *
 * ui-config — presentational only; no data. Iconify serves static official
 * brand SVGs; the fallback tile stays the provider's brand color.
 */
import { useState, type CSSProperties } from "react";

// provider id → Iconify `logos` icon name (https://icon-sets.iconify.design/logos/).
// Only names that resolve upstream should be listed; anything absent (or a
// load failure) renders the brand-glyph fallback.
const LOGOS: Record<string, string> = {
  // Payments
  stripe: "stripe",
  square: "square",
  paypal: "paypal",
  adyen: "adyen",
  // Calendars
  "google-cal": "google-calendar",
  outlook: "microsoft-icon",
  exchange: "microsoft-icon",
  // The Apple Wallet REGISTRATION itself, not just the iCloud/Maps services. Without this the
  // card fell back to the catalog's U+F8FF glyph, which only renders on Apple platforms and
  // showed as an empty box on Windows.
  apple: "apple",
  icloud: "apple",
  // Access management
  entra: "microsoft-azure",
  // Shared app registrations (SHARED_CREDENTIAL_GROUPS logo + feature ids)
  "ms-app": "microsoft-icon",
  msmail: "microsoft-icon",
  msgraphlog: "microsoft-icon",
  // Backend registration provider ids + feature keys (kit 735eab65 shared-credential model)
  google: "google-icon",
  microsoft: "microsoft-icon",
  google_calendar: "google-calendar",
  gmeet: "google-meet",
  msteams: "microsoft-teams",
  meet: "google-meet",
  gmail_send: "google-gmail",
  // Provider-qualified feature-row ids (`${provider}_${feature.key}`) so a feature
  // key like `calendar` resolves per provider (Google Calendar vs MS 365 Calendar).
  google_meet: "google-meet",
  google_gmail_send: "google-gmail",
  google_directory: "google-icon",
  "google-directory": "google-icon",
  microsoft_calendar: "microsoft-icon",
  microsoft_teams: "microsoft-teams",
  microsoft_entra: "microsoft-azure",
  microsoft_email_send: "microsoft-icon",
  microsoft_graph_logging: "microsoft-icon",
  // Video
  zoom: "zoom-icon",
  teams: "microsoft-teams",
  googlemeet: "google-meet",
  // Email / SMS
  sendgrid: "sendgrid-icon",
  twilio: "twilio-icon",
  // CRM
  salesforce: "salesforce",
  hubspot: "hubspot",
  dynamics: "microsoft-icon",
  // Analytics / maps / discovery
  ga: "google-analytics",
  rwg: "google-icon",
  gmaps: "google-maps",
  amaps: "apple",
  // Automation / alerts
  zapier: "zapier-icon",
  slack: "slack-icon",
};

/**
 * Ids whose real mark needs a FALLBACK CHAIN rather than one name: the messaging
 * channels on the Twilio registration (feature rows are `${provider}_${feature.key}`).
 * WhatsApp has an official mark; RCS is Google's RCS Business Messaging, so the
 * Google Messages mark is the honest one; plain SMS has no brand of its own, so the
 * Twilio mark stands for it. Each candidate that fails to load advances to the next,
 * and the brand-glyph tile is still the last resort — an upstream rename degrades
 * instead of breaking.
 */
const WHATSAPP_MARK = ["whatsapp-icon", "whatsapp", "simple-icons/whatsapp?color=%2325D366"];
const SMS_MARK = ["twilio-icon", "simple-icons/twilio?color=%23F22F46"];
const RCS_MARK = ["simple-icons/googlemessages?color=%231A73E8", ...SMS_MARK]; // RCS Business Messaging is Google's; `logos:google-messages` does not exist upstream (checked 2026-08-04)
// Apple publishes no open-source Wallet mark: `logos`, `simple-icons`, `arcticons`,
// `fontisto` and `cbi` all 404 on every apple-wallet spelling (probed against
// api.iconify.design 2026-08-07; Iconify's own search returns exactly ONE hit).
// `iconoir:apple-wallet` is that hit — a monochrome pass-card glyph, rendered black
// by the API since it draws in currentColor. The generic Apple mark stays last so a
// removal upstream degrades instead of dropping to the letter tile.
const APPLE_WALLET_MARK = [
  "iconoir/apple-wallet",
  "logos/apple",
];
/**
 * Ellucian publishes no mark in any open-source icon set, so this candidate is an
 * absolute URL to the vendor's own SVG rather than an Iconify name (supplied
 * 2026-08-19). It is the only candidate, so a fetch failure degrades straight to the
 * brand-colour glyph tile — what the Ethos card rendered before.
 */
const ELLUCIAN_MARK = ["https://logosandtypes.com/wp-content/uploads/2025/05/ellucian.svg"];
const LOGO_CHAINS: Record<string, string[]> = {
  apple: APPLE_WALLET_MARK,
  ethos: ELLUCIAN_MARK,
  twilio_pm_whatsapp: WHATSAPP_MARK,
  twilio_conv_whatsapp: WHATSAPP_MARK,
  whatsapp: WHATSAPP_MARK,
  twilio_pm_sms: SMS_MARK,
  twilio_conv_sms: SMS_MARK,
  twilio_pm_rcs: RCS_MARK,
  rcs: RCS_MARK,
};

export interface ProviderLogoProps {
  id: string;
  name: string;
  brand: string;
  /** fallback glyph when no real mark is available (defaults to first letter) */
  glyph?: string;
  size?: number;
  radius?: number;
  /**
   * Explicit Iconify `logos` names to try, in order, instead of the id lookup —
   * each one that fails to load advances to the next, and the brand-glyph tile
   * is still the final fallback. For callers whose ids aren't catalog provider
   * ids (e.g. the event wizard's meeting providers) and for marks whose upstream
   * name is uncertain, so a rename upstream degrades instead of breaking.
   */
  logoCandidates?: string[];
}

/**
 * A candidate may name its icon SET (and a query), e.g.
 * "simple-icons/webex?color=%2300BCEB" — several real product marks (Webex,
 * GoTo, and Zoom's current mark) simply do not exist in the `logos` set, and
 * the monochrome sets need `?color=` or they render black. A bare name keeps
 * the original `logos/<name>` behaviour, so no existing caller changes.
 */
function iconUrl(spec: string): string {
  // An ABSOLUTE http(s) candidate is used verbatim — for a vendor whose mark exists in
  // no open-source icon set at all (see ELLUCIAN_MARK).
  if (/^https?:\/\//.test(spec)) return spec;
  const qi = spec.indexOf("?");
  const name = qi === -1 ? spec : spec.slice(0, qi);
  const query = qi === -1 ? "" : spec.slice(qi);
  const path = name.includes("/") ? name : `logos/${name}`;
  // ui-config: api.iconify.design is a public, tenant-agnostic icon CDN
  return `https://api.iconify.design/${path}.svg${query}`;
}

/**
 * Wallet-pass tile — drawn locally, not fetched.
 *
 * The Wallet registration and every feature row under it (`apple`, and the
 * provider-qualified `apple_<featureKey>` ids the registration panel builds)
 * need a recognisable pass-stack mark. No open-source set carries one (see
 * APPLE_WALLET_MARK above — the one hit is a flat monochrome glyph), and the
 * letter tile is what the detail page was falling back to. This is our own
 * card-stack drawing on a dark squircle: the pass metaphor, not a copy of any
 * vendor's app artwork.
 *
 * ui-config — presentational only.
 */
function isWalletId(id: string): boolean {
  return id === "apple" || id.startsWith("apple_") || id.startsWith("apple-");
}

function WalletPassMark({ size, radius }: { size: number; radius: number }) {
  return (
    <span style={{ width: size, height: size, borderRadius: radius, flexShrink: 0, display: "inline-flex", overflow: "hidden" }}>
      <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false" style={{ display: "block" }}>
        <rect x="0" y="0" width="48" height="48" rx={Math.round((radius / size) * 48)} fill="#1f2023" />
        <rect x="10" y="9" width="28" height="9" rx="3" fill="#34c759" />
        <rect x="9" y="13" width="30" height="9" rx="3" fill="#0a84ff" />
        <rect x="8" y="17" width="32" height="9" rx="3" fill="#ff6152" />
        <path
          d="M7 26.5a3 3 0 0 1 3-3h6.6a1.6 1.6 0 0 1 1.5 1.1c.8 2.5 2.7 3.7 5.9 3.7s5.1-1.2 5.9-3.7a1.6 1.6 0 0 1 1.5-1.1H38a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3z"
          fill="#e4dece"
        />
      </svg>
    </span>
  );
}

const RADIUS_RATIO = 0.24;
const GLYPH_RATIO = 0.38;
const MARK_RATIO = 0.6; // logo mark occupies ~60% of the tile

export function ProviderLogo({ id, name, brand, glyph, size = 40, radius, logoCandidates }: ProviderLogoProps) {
  const [failedCount, setFailedCount] = useState(0);
  const r = radius ?? Math.round(size * RADIUS_RATIO);
  if (!logoCandidates?.length && isWalletId(id)) return <WalletPassMark size={size} radius={r} />;
  const chain = logoCandidates?.length ? logoCandidates : LOGO_CHAINS[id] ? LOGO_CHAINS[id] : LOGOS[id] ? [LOGOS[id]] : [];
  const logo = chain[failedCount];
  const mark = Math.round(size * MARK_RATIO);

  if (logo) {
    return (
      <span
        style={{
          width: size, height: size, borderRadius: r,
          background: "#f6f7f9", border: "1px solid rgba(17,24,39,0.08)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, overflow: "hidden",
        }}
      >
        <img
          key={logo}
          src={iconUrl(logo)}
          width={mark}
          height={mark}
          alt=""
          aria-hidden="true"
          onError={() => setFailedCount((n) => n + 1)}
          style={{ display: "block", objectFit: "contain" }}
        />
      </span>
    );
  }

  const tile: CSSProperties = {
    width: size, height: size, borderRadius: r, background: brand,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, overflow: "hidden",
  };
  const g = (glyph && glyph.trim()) || (name || "?").trim().charAt(0).toUpperCase();
  return (
    <span style={tile}>
      <span style={{ color: "#fff", fontWeight: 800, fontSize: size * GLYPH_RATIO, letterSpacing: -0.5, fontFamily: "system-ui", lineHeight: 1 }}>
        {g}
      </span>
    </span>
  );
}
